/**
 * Aggregation engine for the pivot table widget.
 *
 * Given raw rows and pivot axis configuration, produces a structure the
 * renderer can traverse to draw:
 *   - Multi-level row headers (with subtotal rows at every level)
 *   - Multi-level column headers
 *   - Per-measure values at every row × column intersection
 *   - Row totals, column totals, grand totals
 *
 * Keeps all the pivoting logic in one place so the Vue component stays
 * focused on rendering + expand/collapse interaction.
 */

const KEY_SEP = '\u0000';

/**
 * Aggregators supported by the widget. Values are reducer identifiers used by
 * `readBucket`. These are internal IDs, not user-facing text.
 */
/* eslint-disable tap/no-raw-text-js -- aggregator identifiers, not UI copy */
export const Aggregator = {
  SUM: 'Sum',
  COUNT: 'Count',
  AVERAGE: 'Average',
  MIN: 'Min',
  MAX: 'Max',
};
/* eslint-enable tap/no-raw-text-js */

/**
 * Aggregates raw rows into a pivot model.
 *
 * @param {Array<Object>} rows
 * @param {Array<string>} rowFields - keys in each row to place on the row axis, in order
 * @param {Array<string>} colFields - keys in each row to place on the column axis, in order
 * @param {Array<{key: string, label: string, aggregator?: string}>} measures
 * @returns {{
 *   rowTree: Node,
 *   colKeys: Array<Array<string>>,
 *   colTree: Node,
 *   grandTotals: Map<string, number>,
 *   getCell: (rowPath: Array<string>, colPath: Array<string>, measureKey: string) => number|null,
 * }}
 *
 * Node shape (for both rowTree and colTree):
 *   {
 *     label: string|null,          // null at the synthetic root
 *     path: Array<string>,          // full path from the root to this node (exclusive of root)
 *     depth: number,                // 0 at root
 *     children: Array<Node>,         // ordered child nodes
 *     isLeaf: boolean,
 *   }
 */
export function buildPivotModel(rows, rowFields, colFields, measures) {
  const rowTree = createNode(null, [], 0);
  const colTree = createNode(null, [], 0);

  // cellStore[rowKey][colKey][measure.key] = running aggregate state
  const cellStore = new Map();
  // rowTotals[rowKey][measure.key] = running aggregate across all cols
  const rowTotals = new Map();
  // colTotals[colKey][measure.key] = running aggregate across all rows
  const colTotals = new Map();
  // grandTotals[measure.key] = running aggregate across everything
  const grandTotals = new Map();

  const ingest = (aggMap, measure, value) => {
    let bucket = aggMap.get(measure.key);
    if (!bucket) {
      bucket = createBucket();
      aggMap.set(measure.key, bucket);
    }
    updateBucket(bucket, value);
  };

  rows.forEach((row) => {
    const rowPath = rowFields.map((field) => stringifyValue(row[field]));
    const colPath = colFields.map((field) => stringifyValue(row[field]));
    const rowKey = rowPath.join(KEY_SEP);
    const colKey = colPath.join(KEY_SEP);

    ensurePath(rowTree, rowPath);
    ensurePath(colTree, colPath);

    if (!cellStore.has(rowKey)) {
      cellStore.set(rowKey, new Map());
    }
    const cellRow = cellStore.get(rowKey);
    if (!cellRow.has(colKey)) {
      cellRow.set(colKey, new Map());
    }
    const cell = cellRow.get(colKey);

    if (!rowTotals.has(rowKey)) {
      rowTotals.set(rowKey, new Map());
    }
    if (!colTotals.has(colKey)) {
      colTotals.set(colKey, new Map());
    }

    measures.forEach((measure) => {
      const rawValue = row[measure.key];
      const value = toNumber(rawValue);
      ingest(cell, measure, value);
      ingest(rowTotals.get(rowKey), measure, value);
      ingest(colTotals.get(colKey), measure, value);
      ingest(grandTotals, measure, value);
    });
  });

  const rowSubtotals = new Map(); // rowKey -> Map<colKey|null, Map<measureKey, bucket>>
  fillRowSubtotals(rowTree, cellStore, rowSubtotals);

  const colLeafKeys = getLeafPaths(colTree);

  /**
   * Returns the aggregated value at (rowPath × colPath × measure). Either
   * path may be a leaf (single row/col) or a non-leaf (subtotal across its
   * descendants — e.g. a collapsed group). All four combinations are
   * supported:
   *   leaf × leaf          — direct cellStore lookup
   *   non-leaf × leaf      — rowSubtotals lookup
   *   leaf × non-leaf      — sum cellStore over col-leaf descendants
   *   non-leaf × non-leaf  — sum rowSubtotals over col-leaf descendants
   */
  const getCell = (rowPathIn, colPathIn, measureKey, aggregator = Aggregator.SUM) => {
    const rowNode = findNode(rowTree, rowPathIn);
    const colNode = findNode(colTree, colPathIn);
    if (!rowNode || !colNode) return null;

    const rowKey = rowPathIn.join(KEY_SEP);
    const colLeafPaths = colNode.isLeaf ? [colNode.path] : getLeafPaths(colNode);

    const accum = createBucket();
    let hasData = false;

    colLeafPaths.forEach((colLeafPath) => {
      const colKey = colLeafPath.join(KEY_SEP);
      const source = rowNode.isLeaf
        ? cellStore.get(rowKey)?.get(colKey)?.get(measureKey)
        : rowSubtotals.get(rowKey)?.get(colKey)?.get(measureKey);
      if (source) {
        accum.sum += source.sum;
        accum.count += source.count;
        if (source.min < accum.min) accum.min = source.min;
        if (source.max > accum.max) accum.max = source.max;
        hasData = true;
      }
    });

    return hasData ? readBucket(accum, aggregator) : null;
  };

  const getRowTotal = (rowPathIn, measureKey, aggregator = Aggregator.SUM) => {
    const rowKey = rowPathIn.join(KEY_SEP);
    const rowNode = findNode(rowTree, rowPathIn);
    if (rowNode?.isLeaf) {
      const bucket = rowTotals.get(rowKey)?.get(measureKey);
      return bucket ? readBucket(bucket, aggregator) : null;
    }
    // Non-leaf subtotal: sum across all col keys at the rolled-up row level.
    const subRow = rowSubtotals.get(rowKey);
    if (!subRow) return null;
    const bucket = subRow.get(null)?.get(measureKey);
    return bucket ? readBucket(bucket, aggregator) : null;
  };

  /**
   * Grand total aligned with a specific col path (leaf or non-leaf). For a
   * leaf path this is the precomputed `colTotals` entry; for a non-leaf
   * (e.g. a collapsed col group) we sum over descendant leaves.
   */
  const getColTotal = (colPathIn, measureKey, aggregator = Aggregator.SUM) => {
    const colNode = findNode(colTree, colPathIn);
    if (!colNode) return null;

    const colLeafPaths = colNode.isLeaf ? [colNode.path] : getLeafPaths(colNode);
    const accum = createBucket();
    let hasData = false;

    colLeafPaths.forEach((colLeafPath) => {
      const colKey = colLeafPath.join(KEY_SEP);
      const source = colTotals.get(colKey)?.get(measureKey);
      if (source) {
        accum.sum += source.sum;
        accum.count += source.count;
        if (source.min < accum.min) accum.min = source.min;
        if (source.max > accum.max) accum.max = source.max;
        hasData = true;
      }
    });

    return hasData ? readBucket(accum, aggregator) : null;
  };

  const getGrandTotal = (measureKey, aggregator = Aggregator.SUM) => {
    const bucket = grandTotals.get(measureKey);
    return bucket ? readBucket(bucket, aggregator) : null;
  };

  return {
    rowTree,
    colTree,
    colLeafKeys,
    getCell,
    getRowTotal,
    getColTotal,
    getGrandTotal,
  };
}

function createNode(label, path, depth) {
  return {
    label,
    path,
    depth,
    children: [],
    isLeaf: true,
  };
}

function ensurePath(root, pathSegments) {
  let node = root;
  pathSegments.forEach((segment, idx) => {
    let child = node.children.find((c) => c.label === segment);
    if (!child) {
      child = createNode(segment, [...node.path, segment], idx + 1);
      node.children.push(child);
      node.isLeaf = false;
    }
    node = child;
  });
}

function findNode(root, path) {
  let node = root;
  for (let i = 0; i < path.length; i += 1) {
    node = node?.children.find((c) => c.label === path[i]);
    if (!node) return null;
  }
  return node;
}

function getLeafPaths(node) {
  if (node.isLeaf) {
    return [node.path];
  }
  return node.children.flatMap(getLeafPaths);
}

function fillRowSubtotals(rowTree, cellStore, rowSubtotals) {
  function walk(node) {
    if (node.isLeaf) {
      return;
    }
    node.children.forEach(walk);

    const nodeKey = node.path.join(KEY_SEP);
    const agg = new Map(); // colKey | null -> Map<measureKey, bucket>
    agg.set(null, new Map());

    node.children.forEach((child) => {
      const childKey = child.path.join(KEY_SEP);
      const childIsLeaf = child.isLeaf;
      const childCells = childIsLeaf ? cellStore.get(childKey) : rowSubtotals.get(childKey);
      if (!childCells) return;

      childCells.forEach((measureMap, colKey) => {
        // For leaves, only "real" col keys are present — no null key.
        // For non-leaves, null key carries the row's own all-col total.
        if (!agg.has(colKey)) {
          agg.set(colKey, new Map());
        }
        const target = agg.get(colKey);
        measureMap.forEach((bucket, measureKey) => {
          mergeBucket(target, measureKey, bucket);
        });

        if (childIsLeaf) {
          // Merge into the null (all-col) entry too.
          const nullTarget = agg.get(null);
          measureMap.forEach((bucket, measureKey) => {
            mergeBucket(nullTarget, measureKey, bucket);
          });
        }
      });
    });

    rowSubtotals.set(nodeKey, agg);
  }

  walk(rowTree);
}

function createBucket() {
  return { sum: 0, count: 0, min: Infinity, max: -Infinity };
}

function updateBucket(bucket, value) {
  bucket.sum += value;
  bucket.count += 1;
  if (value < bucket.min) bucket.min = value;
  if (value > bucket.max) bucket.max = value;
}

function mergeBucket(target, measureKey, source) {
  let bucket = target.get(measureKey);
  if (!bucket) {
    bucket = createBucket();
    target.set(measureKey, bucket);
  }
  bucket.sum += source.sum;
  bucket.count += source.count;
  if (source.min < bucket.min) bucket.min = source.min;
  if (source.max > bucket.max) bucket.max = source.max;
}

function readBucket(bucket, aggregator) {
  switch (aggregator) {
    case Aggregator.COUNT:
      return bucket.count;
    case Aggregator.AVERAGE:
      return bucket.count ? bucket.sum / bucket.count : 0;
    case Aggregator.MIN:
      return bucket.min === Infinity ? 0 : bucket.min;
    case Aggregator.MAX:
      return bucket.max === -Infinity ? 0 : bucket.max;
    case Aggregator.SUM:
    default:
      return bucket.sum;
  }
}

function toNumber(value) {
  if (value === null || value === undefined || value === '') return 0;
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
}

function stringifyValue(value) {
  if (value === null || value === undefined) return '';
  return String(value);
}
